import React, { Fragment, useEffect, useRef, useState } from 'react'
import { CollapsibleMenuPropsTypes, ItemTypes } from './CollapsibleMenuTypesNew'
import { useLocation, useNavigate } from 'react-router-dom'
import { DownIcon, UpIcon } from '../iconSet/IconSet'


const CollapsibleMenuNew = (props: CollapsibleMenuPropsTypes) => {

    const location = useLocation()
    const navigate = useNavigate()
    const menuRef = useRef<HTMLDivElement | null>(null)
    
    // Get URLSearchParams to handle query parameters
    const searchParams = new URLSearchParams(location.search)
    const currentModule = searchParams.get('module')

    // Initialize with early_warning_main expanded by default
    const [expandedItems, setExpandedItems] = useState<Set<string>>(
        new Set(props.defaultExpandedItemKey ? [props.defaultExpandedItemKey] : ['early_warning_main'])
    )

    // Initialize expanded state based on current location AND ensure early_warning_main is expanded
    useEffect(() => {
        const findAndExpandParents = (items: ItemTypes[], targetLink: string): string[] => {
            const expandedKeys: string[] = []
            
            const searchItems = (items: ItemTypes[], path: string[] = []): boolean => {
                for (const item of items) {
                    const currentPath = [...path, item.key]
                    
                    // Check using BOTH methods
                    if (item.link === targetLink || isItemActiveWithQuery(item)) {
                        expandedKeys.push(...currentPath)
                        return true
                    }
                    
                    if (item.children && searchItems(item.children, currentPath)) {
                        expandedKeys.push(...currentPath)
                        return true
                    }
                }
                return false
            }
            
            searchItems(items)
            return expandedKeys
        }

        const keysToExpand = findAndExpandParents(props.items, location.pathname)
        
        // Always include early_warning_main in the expanded items
        const finalKeysToExpand = [...keysToExpand, 'early_warning_main']
        
        if (finalKeysToExpand.length > 0) {
            setExpandedItems(new Set(finalKeysToExpand))
        }
    }, [location.pathname, location.search, props.items])

    // Alternative approach: If you want to specifically find and expand early_warning_main
    useEffect(() => {
        const ensureEarlyWarningExpanded = (items: ItemTypes[]) => {
            const findItem = (items: ItemTypes[]): boolean => {
                for (const item of items) {
                    if (item.key === 'early_warning_main') {
                        setExpandedItems(prev => new Set([...prev, 'early_warning_main']))
                        return true
                    }
                    if (item.children && findItem(item.children)) {
                        return true
                    }
                }
                return false
            }
            findItem(items)
        }

        ensureEarlyWarningExpanded(props.items)
    }, [props.items])

    // Method 1: Original exact pathname match
    const isItemActiveExact = (item: ItemTypes): boolean => {
        return item.link === location.pathname
    }

    // Method 2: Query parameter based match
    const isItemActiveWithQuery = (item: ItemTypes): boolean => {
        if (!item.link) return false
        
        // For items with query parameters like "/training?module=MOD-00001"
        if (item.link.includes('?')) {
            const [itemPath, itemQuery] = item.link.split('?')
            const itemModule = new URLSearchParams(itemQuery).get('module')
            
            return location.pathname === itemPath && currentModule === itemModule
        }
        
        return false
    }

    // Combined method: Check both ways
    const isItemActive = (item: ItemTypes): boolean => {
        return isItemActiveExact(item) || isItemActiveWithQuery(item)
    }

    const toggleExpand = (key: string) => {
        setExpandedItems(prev => {
            const newSet = new Set(prev)
            if (newSet.has(key)) {
                newSet.delete(key)
                // Only collapse direct children, not grandchildren
                const collapseDirectChildren = (items: ItemTypes[]) => {
                    items.forEach(item => {
                        if (item.children) {
                            item.children.forEach(child => {
                                newSet.delete(child.key)  // This removes ALL direct children
                            })
                        }
                    })
                }
                
                const findAndCollapseDirect = (items: ItemTypes[]) => {
                    items.forEach(item => {
                        if (item.key === key && item.children) {
                            collapseDirectChildren([item])
                        } else if (item.children) {
                            findAndCollapseDirect(item.children)
                        }
                    })
                }
                findAndCollapseDirect(props.items)
            } else {
                newSet.add(key)
            }
            return newSet
        })
    }

    const isExpanded = (key: string) => expandedItems.has(key)

    const handleNavigate = (link: string) => {
        if (link) {
            navigate(link)
        }
    }

    const hasActiveChild = (items: ItemTypes[]): boolean => {
        for (const item of items) {
            // Use combined method to check for active children
            if (isItemActive(item)) return true
            if (item.children && hasActiveChild(item.children)) return true
        }
        return false
    }

    // Check if an item is a leaf node (has no children)
    const isLeafNode = (item: ItemTypes): boolean => {
        return !item.children || item.children.length === 0
    }

    const renderMenuItems = (items: ItemTypes[], level = 0) => {
        return items.map((item, itemIndex) => {
            const hasChildren = item.children && item.children.length > 0
            const isItemExpanded = isExpanded(item.key)
            const isActive = isItemActive(item) // Use combined method
            const hasActiveChildren = hasChildren && hasActiveChild(item.children!)
            const isLeaf = isLeafNode(item)
            
            // Calculate padding - first level has more padding than second level
            const paddingLeft = level === 1 ? '2rem' : `${1 + level * 1}rem`
            const marginLeft = level === 0 ? '0' : level === 1 ? '1rem' : '2rem'
            
            const itemStyle = {
                backgroundColor: isActive && isLeaf
                    ? 'var(--color-side-menu-sub-item-select-bg)' 
                    : !isLeaf
                    ? 'var(--color-side-menu-main-item-bg)'
                    : 'transparent',
                paddingLeft: '0.5rem',
                borderLeft: level > 0 ? '2px solid var(--color-side-menu-border)' : 'none',
                marginLeft: marginLeft
            }

            return (
                <Fragment key={item.key}>
                    <div 
                        className={`menu-item level-${level}`}
                        style={itemStyle}
                        onClick={(e) => {
                            e.stopPropagation()
                            if (hasChildren) {
                                toggleExpand(item.key)
                            } else if (item.link) {
                                handleNavigate(item.link)
                            }
                        }}
                    >
                        {hasChildren && (
                            <span className="expand-icon">
                                {isItemExpanded ? 
                                    <UpIcon width='0.8rem' color='var(--color-text)' /> : 
                                    <DownIcon width='0.8rem' color='var(--color-text)' />
                                }
                            </span>
                        )}
                        
                        {isLeaf && (
                            <span className="icon-wrapper">
                                {React.cloneElement(item.icons, {
                                    color: isActive ? 'var(--color-main-white)' : 'var(--color-text)',
                                    width: `${1.2 - level * 0.1}rem`
                                })}
                            </span>
                        )}
                        
                        <span 
                            className={`label ${isActive ? 'active' : ''}`}
                            style={{
                                color: isActive ? 'var(--color-main-white)' : 'var(--color-text)',
                                marginLeft: hasChildren ? '0' : isLeaf ? '0' : '0'
                            }}
                        >
                            {item.label}
                        </span>
                    </div>

                    {hasChildren && (
                        <div 
                            className={`children-wrapper level-${level}`}
                            style={{
                                height: isItemExpanded ? 'auto' : '0',
                                overflow: 'hidden'
                            }}
                        >
                            {renderMenuItems(item.children!, level + 1)}
                        </div>
                    )}
                </Fragment>
            )
        })
    }

    return (
        <div className={`collapsible-menu-wrapper level-${props.level || 0}`}>
            {props.type && (
                <span className={`group-name`}>{props.type}</span>
            )}
            <div className="menu-items-container">
                {renderMenuItems(props.items, props.level || 0)}
            </div>
        </div>
    )
}

export default CollapsibleMenuNew